import { useState, useEffect } from "react";
import { contactSupport, getTickets, replyMyTicket, apiError } from "@/lib/api";
import { TicketThread } from "@/components/TicketThread";
import { toast } from "sonner";
import { Loader2, LifeBuoy } from "lucide-react";
import { logWarn } from "@/lib/log";

const SupportForm = ({ onCreated }) => {
    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const ticket = await contactSupport({ subject, message });
            onCreated(ticket);
            setSubject("");
            setMessage("");
            toast.success("Support request sent — we'll get back to you");
        } catch (err) {
            toast.error(apiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <form onSubmit={submit} className="border border-white/10 bg-surface p-6 mb-10 space-y-4">
            <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 block mb-2">Subject</label>
                <input
                    required maxLength={150} value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="What do you need help with?"
                    className="w-full bg-obsidian border border-white/10 text-white px-3 py-3 font-mono text-sm outline-none focus:border-electric placeholder:text-white/20 transition-colors"
                    data-testid="support-subject-input"
                />
            </div>
            <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 block mb-2">Message</label>
                <textarea
                    required rows={6} maxLength={4000} value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Describe your issue in detail..."
                    className="w-full bg-obsidian border border-white/10 text-white px-3 py-3 font-mono text-sm outline-none focus:border-electric placeholder:text-white/20 resize-none transition-colors"
                    data-testid="support-message-input"
                />
            </div>
            <button
                type="submit" disabled={busy}
                className="bg-blaze hover:bg-blazeHover disabled:opacity-50 text-white font-mono uppercase text-[11px] tracking-[0.25em] px-8 py-3.5 flex items-center gap-2 transition-colors"
                data-testid="support-submit-button"
            >
                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Send Message →
            </button>
        </form>
    );
};

const TicketItem = ({ ticket, onReply }) => (
    <div className="py-4" data-testid={`ticket-${ticket.ticket_id}`}>
        <div className="flex items-center justify-between gap-3 mb-1">
            <div className="font-mono text-sm text-white/90 font-bold">{ticket.subject}</div>
            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-blaze border border-blaze/40 px-2 py-0.5">
                {ticket.status}
            </span>
        </div>
        <div className="font-mono text-xs text-white/50 line-clamp-2">{ticket.message}</div>
        <div className="font-mono text-[10px] text-white/30 mt-1">
            {new Date(ticket.created_at).toLocaleString()}
        </div>
        <TicketThread ticket={ticket} viewerRole="user" onReply={onReply} />
    </div>
);

const TicketsList = ({ tickets, onReplied }) => (
    <div className="border border-white/10 bg-surface p-6" data-testid="support-tickets-list">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60 mb-4 flex items-center gap-2">
            <LifeBuoy className="w-3.5 h-3.5 text-blaze" />
            Your Tickets
        </div>
        {tickets.length === 0 ? (
            <div className="font-mono text-xs text-white/30 py-2">No tickets yet.</div>
        ) : (
            <div className="divide-y divide-white/5">
                {tickets.map((t) => (
                    <TicketItem
                        key={t.ticket_id}
                        ticket={t}
                        onReply={async (message) => {
                            const fresh = await replyMyTicket(t.ticket_id, message);
                            onReplied(fresh);
                            toast.success("Reply sent to support");
                        }}
                    />
                ))}
            </div>
        )}
    </div>
);

export default function Support() {
    const [tickets, setTickets] = useState([]);

    useEffect(() => {
        getTickets().then(setTickets).catch((e) => logWarn("tickets load failed", e));
    }, []);

    return (
        <div className="max-w-3xl" data-testid="support-page">
            <div className="mb-8">
                <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-blaze">/ Support</div>
                <h1 className="font-display font-bold uppercase text-3xl mt-2 tracking-tight">Contact Support</h1>
            </div>
            <SupportForm onCreated={(ticket) => setTickets((prev) => [ticket, ...prev])} />
            <TicketsList
                tickets={tickets}
                onReplied={(fresh) =>
                    setTickets((prev) => prev.map((x) => (x.ticket_id === fresh.ticket_id ? fresh : x)))
                }
            />
        </div>
    );
}
